import { type EntryPoint, type EntryPointComponent, type PreloadFetchPolicy } from 'react-relay';

import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import parameters, {
    type UserProfileCardEntryPointQuery,
	type UserProfileCardEntryPointQuery$variables,
} from './__generated__/UserProfileCardEntryPointQuery.graphql';

const fetchPolicy: PreloadFetchPolicy = 'store-or-network';

/**
 * Arguments passed to `entryPoint` `getPreloadProps` / `UserProfileCardWithTrigger`
 * (subject user, viewer, tenant). `cloudId` is the site id used as `singleUserTenantId`.
 */
export type UserProfileCardRelayTriggerParams = {
	accountId: string;
	cloudId: string;
	requestingUserId: string;
};

const entryPoint: EntryPoint<EntryPointComponent<{
    queryRef: UserProfileCardEntryPointQuery;
}, Record<string, never>, {
    cloudId: string;
    testId?: string | undefined;
}, Record<string, never>>, UserProfileCardRelayTriggerParams> = createEntryPoint({
	root: JSResourceForInteraction(
		() =>
			import(
				/* webpackChunkName: "@atlaskit-internal_user-profile-card-entrypoint" */ './UserProfileCardEntryPoint'
			),
		{
			moduleId: 'user-profile-card-entrypoint',
		},
	),
	getPreloadProps: ({
		accountId,
		cloudId,
		requestingUserId,
	}: UserProfileCardRelayTriggerParams) => {
		const accountIdARI = `ari:cloud:identity::user/${accountId}`;
		const variables: UserProfileCardEntryPointQuery$variables = {
			first: 10,
			filter: { actors: [accountIdARI] },
			singleUserAccountId: accountId,
			singleUserRequestingUserId: requestingUserId,
			singleUserTenantId: cloudId,
		};
		return {
			queries: {
				queryRef: {
					parameters,
					variables,
					options: {
						fetchPolicy,
					},
				},
			},
		};
	},
});

export { entryPoint };
